import { useEffect, useRef, useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Module-scoped cache so every page in a session shares one fetch.
let _cache = null;
let _inflight = null;

/**
 * Loads the platform-wide species default map once per session and keeps
 * it cached. Safe to call from many components — the actual HTTP request
 * fires only once.
 *
 * Shape:
 *   {
 *     Seabream: { genetic_origin, fcr_version_id, model_source, temperature_region },
 *     Seabass:  { genetic_origin, fcr_version_id, model_source, temperature_region },
 *     Tilapia:  { fcr_version_id, model_source },
 *   }
 */
export function useSpeciesDefaults(token) {
  const [defaults, setDefaults] = useState(_cache);

  useEffect(() => {
    if (_cache) { setDefaults(_cache); return; }
    if (!token) return;
    if (!_inflight) {
      _inflight = axios
        .get(`${BACKEND_URL}/api/species-defaults`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((r) => { _cache = r.data; return r.data; })
        .catch(() => { _cache = null; return null; })
        .finally(() => { _inflight = null; });
    }
    _inflight.then((data) => { if (data) setDefaults(data); });
  }, [token]);

  return defaults;
}

/**
 * Resolve the canonical species key from any of the various forms used in
 * the platform: 'Sparus aurata', 'Dicentrarchus labrax', 'Tilapia',
 * 'Seabream', 'Seabass', oreochromis-style strings, etc.
 */
export function canonicalSpeciesKey(speciesValue) {
  if (!speciesValue) return null;
  const s = String(speciesValue).toLowerCase();
  if (s.includes('aurata') || s.includes('seabream') || s.includes('sea bream')) return 'Seabream';
  if (s.includes('labrax') || s.includes('seabass') || s.includes('sea bass')) return 'Seabass';
  if (s.includes('tilapia') || s.includes('oreochromis')) return 'Tilapia';
  if (s.includes('tripletail') || s.includes('triple tail')) return 'Tripletail';
  return null;
}

/**
 * useApplySpeciesDefaults — drop into a module page that has a
 * `formData.species` field. Whenever the species *changes* (and is one of
 * our supported keys), it merges the platform defaults into the form via
 * the provided `applyKeys` handler.
 *
 * Usage:
 *   useApplySpeciesDefaults({
 *     token,
 *     species: formData.species,
 *     keys: ['genetic_origin', 'fcr_version_id', 'temperature_region', 'model_source'],
 *     onApply: (patch) => setFormData(prev => ({ ...prev, ...patch })),
 *   });
 *
 * Behaviour:
 *   - Triggers ONLY on transitions of the canonical species key (so changing
 *     the species from Seabream → Seabream-with-genetic-origin-tweak does
 *     NOT clobber user edits).
 *   - Skips applying any key whose default is empty/missing — preserves
 *     existing user input where the platform has no opinion (e.g. Tilapia's
 *     temperature_region is left alone).
 *   - Skips applying on the very first mount when the species was already
 *     set (e.g. opening a saved record). Only NEW selections trigger.
 */
export function useApplySpeciesDefaults({ token, species, keys, onApply }) {
  const defaults = useSpeciesDefaults(token);
  const lastAppliedRef = useRef(null);
  const initialMountRef = useRef(true);

  useEffect(() => {
    const canonical = canonicalSpeciesKey(species);
    if (!canonical || !defaults || !defaults[canonical]) return;
    // Skip on first mount — only apply when user actively changes species.
    if (initialMountRef.current) {
      initialMountRef.current = false;
      lastAppliedRef.current = canonical;
      return;
    }
    if (lastAppliedRef.current === canonical) return;
    lastAppliedRef.current = canonical;

    const fullDefaults = defaults[canonical];
    const patch = {};
    (keys || Object.keys(fullDefaults)).forEach((k) => {
      const v = fullDefaults[k];
      if (v !== undefined && v !== null && v !== '') {
        patch[k] = v;
      }
    });
    if (Object.keys(patch).length) onApply(patch);
  }, [species, defaults, keys, onApply]);
}
